import { type Component } from 'solid-js';

/**
 * The gold badge: a coin and a number, in evidence.
 *
 * ── Why it is prominent, and why it is not in the town bar ──────────────────
 *
 * Gold was a small figure tucked at the end of the town bar, floating over the world with
 * nothing around it to give it meaning. Reported as *weird* — and it was, because a purse
 * is only ever consulted in two places: **the shop**, where it is the constraint on what
 * you can buy, and **the bag**, where it is what a full inventory converts into. Beside a
 * row of workshop icons it was neither.
 *
 * So it is drawn where it is used, at a size that says it is a headline rather than a
 * footnote. One component for both, because two would be two sets of type sizes and two
 * coins that drift apart.
 */
export const Purse: Component<{
  gold: number;
  /**
   * A caption under the figure — *your gold*, *banked*. Absent for the compact form, where
   * the surrounding label already says what it is.
   */
  label?: string;
  /** The compact form: inline, for a line of running text rather than a headline. */
  small?: boolean;
  testId?: string;
}> = (props) => (
  <span
    class="purse-badge"
    classList={{ 'purse-badge--small': props.small }}
    data-testid={props.testId ?? 'purse'}
  >
    {/* Decoration: the number beside it is the information, and a screen reader reading
        "coin 145" over "145 gold" would be worse. The label carries the noun. */}
    <span class="purse-badge__coin" aria-hidden="true" />
    <span class="purse-badge__figures">
      <strong class="purse-badge__value">{props.gold.toLocaleString()}</strong>
      {props.label ? <span class="purse-badge__label">{props.label}</span> : null}
    </span>
  </span>
);
